import { Select } from 'antd';
import { observer } from 'mobx-react';
import React, { useEffect } from 'react';

import { ifTemplate } from 'stores/DynamicProperties/casts';
import { maybeAddChangeListener } from 'stores/DynamicProperties/changeListeners';
import { type IDynamicProperty } from 'stores/DynamicProperties/IDynamicProperty';
import { getOwningCreative } from 'stores/DynamicProperties/tree';
import { useForceUpdate } from 'utils/Hooks';

import { ScalarContainer } from './ScalarContainer';

const { Option } = Select;

export interface TemplateInputProps {
  property: IDynamicProperty<any>;
  disabled?: boolean;
}

function TemplateInput({
  property: uncheckedProperty,
  disabled,
}: TemplateInputProps) {
  const forceUpdate = useForceUpdate();

  const property = ifTemplate(uncheckedProperty);
  const templateSet = property && getOwningCreative(property)?.templates;

  useEffect(
    () => maybeAddChangeListener(property, forceUpdate),
    [property, forceUpdate]
  );

  const handleChange = (value: string) => {
    if (templateSet && property) {
      const changedTemplate = templateSet.getById(value);
      if (changedTemplate) {
        property.set(value);
      }
    }
  };

  const options = [
    <Option key={'(none)'} value={''}>
      {'Select None'}
    </Option>,

    ...(templateSet?.children ?? []).map((item) => (
      <Option key={item.id} value={item.id}>
        {item.data.name}
      </Option>
    )),
  ];

  const currentId = property && templateSet?.getById(property.value)?.id;

  return (
    <ScalarContainer size="full">
      <Select
        showSearch
        style={{ width: '100%' }}
        value={currentId}
        onChange={handleChange}
        placeholder="Select a template"
        disabled={disabled || !property}
        // Required, not decorative: each option's `value` is a template id, and
        // the default search matches on `value`. Without this, typing would
        // search Firestore ids rather than the names on screen.
        optionFilterProp="children"
      >
        {options}
      </Select>
    </ScalarContainer>
  );
}

export default observer(TemplateInput);
